import React, { useState, useEffect, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/appContext";
import TokenService from "../utils/TokenService";
import MainMenu from "../components/MainMenu";
import authApi from "../service/IdApi";
import DataUtils from "../utils/DataUtils";
import { throttle } from "lodash";
import loginApi from "../service/LoginApi";

const Verify2FA = () => {
    const { displayAlert } = useAppContext();
    const navigate = useNavigate();
    const [existing2FAMethods, setExisting2FAMethods] = useState([]);
    const [isLoading2FAMethods, setIsLoading2FAMethods] = useState(true);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [twoFactorVerificationMethod, setTwoFactorVerificationMethod] = useState("sms");
    const [code, setCode] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [invalidCode, setInvalidCode] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [mobileTopbarActive] = useState(false);

    useEffect(() => {
        if (!TokenService.isAuthenticated()) {
            TokenService.setRedirectOnLogin(window.location.href);
            navigate("/");
            return;
        }
        if (TokenService.isLoggedIn() && !TokenService.isPending2fa()) {
            if (!TokenService.isUserProfileComplete()) {
                navigate("/complete-profile");
            } else {
                navigate("/account/select-provider");
            }
            return;
        }
        authApi.get("auth/2fa/methods")
            .then((response) => {
                const methods = response.data?.methods || [];
                setExisting2FAMethods(methods);
            })
            .catch(() => {
                displayAlert("error", "Error", "Failed to load verification methods. Please try again.");
            })
            .finally(() => {
                setIsLoading2FAMethods(false);
            });
    }, [navigate, displayAlert]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => {
            setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const handleSelectMethod = throttle(async (method, isResend = false) => {
        if (isSendingCode || (!isResend && codeSent)) return;
        if (!isResend) setSelectedMethod(method);
        if (!isResend) setTwoFactorVerificationMethod(method.type);
        try {
            setIsSendingCode(true);
            await authApi.post("auth/2fa/send-code-login", {
                method: method.type,
                phoneNumber: method.phoneNumber,
            });
            if (!isResend) setCodeSent(true);
            setResendCooldown(60);
            displayAlert("success", "Success", "Verification code sent");
        } catch (error) {
            const msg = error?.response?.data?.msg || error?.response?.data?.message || "";
            if (msg.includes("expired")) {
                displayAlert("error", "Error", "Verification code has expired. Please request a new code.");
            } else {
                displayAlert("error", "Error", "Failed to send verification code. Please try again.");
            }
        } finally {
            setIsSendingCode(false);
        }
    }, 500, { trailing: false });

    const handleResendCode = () => {
        if (!selectedMethod || isSendingCode || resendCooldown > 0) return;
        handleSelectMethod(selectedMethod, true);
    };

    const contactsByNumber = useMemo(() => {
        const byNumber = new Map();
        (existing2FAMethods || []).forEach((method) => {
            const key = method.phoneNumber || "";
            if (!byNumber.has(key)) byNumber.set(key, { phoneNumber: method.phoneNumber });
        });
        return Array.from(byNumber.values());
    }, [existing2FAMethods]);

    const formatMaskedPhone = (phoneNumber) => {
        if (!phoneNumber) return "";
        const phoneDisplay = phoneNumber.replace(/^\+1/, "");
        const formattedPhone = DataUtils.formatPhoneNumber(phoneDisplay);
        const digits = (formattedPhone.match(/\d/g) || []).join("");
        const last4 = digits.slice(-4);
        let digitIndex = 0;
        return formattedPhone.replace(/\d/g, () => (++digitIndex <= digits.length - 4 ? "*" : last4[digitIndex - (digits.length - 4) - 1] ?? ""));
    };

    const handleBackToMethodSelection = (e) => {
        e?.preventDefault();
        setCodeSent(false);
        setCode("");
        setInvalidCode(false);
        setResendCooldown(0);
        setSelectedMethod(null);
    };

    const handleVerify = async (e) => {
        e?.preventDefault();
        setInvalidCode(false);
        if (!code || code.trim().length !== 6) {
            displayAlert("error", "Error", "Please enter the 6-digit verification code");
            setInvalidCode(true);
            return;
        }
        try {
            setIsVerifying(true);
            const response = await authApi.post("auth/2fa/verify-login", {
                code: code.trim(),
                method: twoFactorVerificationMethod,
            });
            const data = response.data;
            if (data?.userAccessToken && data?.userRefreshToken) {
                TokenService.setAccessToken(data.userAccessToken);
                TokenService.setRefreshToken(data.userRefreshToken);
                if (!TokenService.isUserProfileComplete()) {
                    navigate("/complete-profile");
                } else {
                    TokenService.redirectUser(data.providerCount);
                }
            }
        } catch (error) {
            setIsVerifying(false);
            const msg = error?.response?.data?.msg || error?.response?.data?.message || "";
            if (msg.includes("expired")) {
                displayAlert("error", "Error", "Verification code has expired. Please request a new code.");
            } else {
                displayAlert("error", "Error", "Invalid verification code. Please try again.");
            }
            setInvalidCode(true);
        }
    };

    const layoutContainerClassName = classNames(
        "layout-wrapper",
        "pages-body",
        "login-page",
        "flex",
        "flex-column",
        "layout-menu-cyan layout-topbar-cyan",
        {
            "layout-topbar-mobile-active": mobileTopbarActive,
            "p-input-outlined": "outlined",
        }
    );

    if (!TokenService.isAuthenticated() || (TokenService.isLoggedIn() && !TokenService.isPending2fa())) {
        return null;
    }

    const backToLoginClicked = throttle(async () => {
        try {
            await loginApi.get("auth/logout");
            TokenService.logoutUser();
        } catch (error) {
            TokenService.logoutUser();
        }
        navigate("/login");
    }, 500, { trailing: false });

    return (
        <div className={layoutContainerClassName}>
            <MainMenu />
            <div className="align-self-center mt-auto mb-auto w-full" style={{ maxWidth: "500px" }}>
                <div className="pages-panel card flex flex-column">
                    <div className="pages-header mb-2 px-3 py-2">
                        <h2>Account Verification</h2>
                    </div>
                    {isLoading2FAMethods ? (
                        <div className="px-6 pb-6 text-center">Loading verification methods...</div>
                    ) : (
                        <form className="form" onSubmit={handleVerify}>
                            <div className="input-panel flex flex-column px-0 sm:px-3">
                                {!codeSent ? (
                                    <>
                                        {contactsByNumber.length > 0 ? (
                                            <>
                                                <div className="field col-12 mb-3">
                                                    <label className="mb-3 block text-lg text-left">Please select an option below to receive a 6-digit verification code by text message or voice call.</label>
                                                    <div className="flex flex-column gap-3">
                                                        {contactsByNumber.map((contact, contactIndex) => {
                                                            const maskedPhone = formatMaskedPhone(contact.phoneNumber);
                                                            const smsMethod = { type: "sms", phoneNumber: contact.phoneNumber };
                                                            const voiceMethod = { type: "voice", phoneNumber: contact.phoneNumber };
                                                            return (
                                                                <div
                                                                    key={contactIndex}
                                                                    className="p-3 border-round border-1 surface-border"
                                                                >
                                                                    {maskedPhone && (
                                                                        <div className="font-medium mb-3 text-700 text-lg">{maskedPhone}</div>
                                                                    )}
                                                                    <div className="flex flex-wrap gap-2 justify-content-center">
                                                                        <Button
                                                                            type="button"
                                                                            label="Text Message (SMS)"
                                                                            icon="pi pi-envelope"
                                                                            className={classNames("p-button-outlined", { "p-button-primary": selectedMethod?.phoneNumber === contact.phoneNumber && selectedMethod?.type === "sms" })}
                                                                            onClick={() => handleSelectMethod(smsMethod)}
                                                                            loading={isSendingCode}
                                                                            disabled={isSendingCode}
                                                                        />
                                                                        <Button
                                                                            type="button"
                                                                            label="Voice Call"
                                                                            icon="pi pi-phone"
                                                                            className={classNames("p-button-outlined", { "p-button-primary": selectedMethod?.phoneNumber === contact.phoneNumber && selectedMethod?.type === "voice" })}
                                                                            onClick={() => handleSelectMethod(voiceMethod)}
                                                                            loading={isSendingCode}
                                                                            disabled={isSendingCode}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="flex flex-row justify-content-between mt-3">
                                                        <Button type="button" label="Back to login" icon="pi pi-arrow-left" className="p-button-text text-sm sm:text-base" onClick={backToLoginClicked} />
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="text-center py-4">No verification methods found. Please contact support.</div>
                                                <div className="flex flex-row justify-content-between mt-3">
                                                    <Button type="button" label="Back to login" icon="pi pi-arrow-left" className="p-button-text text-sm sm:text-base" onClick={backToLoginClicked} />
                                                </div>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="mt-3 mb-5 text-left text-lg">Enter the 6-digit verification code we sent to your phone.</div>
                                        <div className="p-inputgroup mb-3">
                                            <span className="p-float-label">
                                                <InputText
                                                    id="code"
                                                    type="tel"
                                                    value={code}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                                                        setCode(value);
                                                        setInvalidCode(false);
                                                    }}
                                                    className={classNames({ "p-invalid": invalidCode })}
                                                    placeholder="000000"
                                                    maxLength={6}
                                                    autoFocus
                                                    inputMode="numeric"
                                                    style={{
                                                        fontSize: "1.5rem",
                                                        letterSpacing: "0.5rem",
                                                        textAlign: "center",
                                                        fontFamily: "monospace",
                                                    }}
                                                />
                                                <label htmlFor="code">Verification Code</label>
                                            </span>
                                        </div>
                                        {resendCooldown > 0 ? (
                                            <div className="text-700 text-sm mb-2">
                                                Request a new code in {resendCooldown}s
                                            </div>
                                        ) : (
                                            <div>
                                                <Button
                                                    type="button"
                                                    label="Resend"
                                                    icon="pi pi-refresh"
                                                    className="p-button-text mb-3"
                                                    onClick={handleResendCode}
                                                    disabled={isSendingCode}
                                                />
                                            </div>
                                        )}
                                        <div className="flex flex-row justify-content-between mt-3">
                                            <div>
                                                <Button type="button" label="Back to login" icon="pi pi-arrow-left" className="p-button-text text-sm sm:text-base" onClick={backToLoginClicked} />
                                            </div>
                                            <div>
                                                <Button
                                                    label="Verify & Continue"
                                                    icon="pi pi-check"
                                                    type="submit"
                                                    disabled={!code || code.trim().length !== 6 || isVerifying}
                                                    loading={isVerifying}
                                                />
                                            </div>
                                        </div>

                                    </>
                                )}
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Verify2FA;
